import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { queryOptions, type QueryClient } from "@-/com.npmjs.tanstack__react-query";
import { useCallback } from "react";
import type { UserCardStage } from "@-/dev.improve.haniverse.constants.user-card-stage";
import {
  getMinUserCardStage,
  getUserCardStage,
} from "@-/dev.improve.haniverse.lib.cards.user-card.mastery";
import {
  getMorphemeGlyphTileStageFromUserCardStage,
  type MorphemeGlyphTileStage,
} from "#morpheme-tile-stage";
import { hasLength } from "@-/com.npmjs.ts-array-length";

const UNADDED_STAGE: MorphemeGlyphTileStage = "unadded";

type StagedUserCard = {
  fsrsCard: { lastReview: string | null; scheduledDays: number } | null;
  manuallyMasteredAt: string | null;
};

/**
 * grapheme → its SRS button stage; graphemes without an active study card or manual mastery
 * resolve to `"unadded"`, even when a detached user-card row remains to preserve user-authored data.
 */
export type MorphemeStagesByGrapheme = Readonly<Record<string, MorphemeGlyphTileStage>>;

// Every table a grapheme's stage is read through: `user_grapheme_*_card` carry `grapheme`
// directly, and `user_card` / `fsrs_card` reach it via
//   fsrs_card.id → user_card.fsrs_card_id → user_card.id → user_grapheme_*_card.user_card_id
//
// A committed write to ANY of them invalidates every registered stage read, whichever grapheme it
// touched. A stage read used to resolve each event's `rowId` back to its grapheme and invalidate
// only the sets containing it, but the change feed cannot deliver that precision: the actor
// connection is Turso, which has no SQLite update hook, so the only events come from
// `attachSqliteChangeFeed`, whose `rowId` is the connection's `lastInsertRowId`. That is correct
// for a single-row INSERT and nothing else. Every UPDATE (each rating rewrites `fsrs_card`; an
// enrollment links its schedule with `UPDATE user_card … RETURNING`) and every pull from another
// device reported some other row or 0, so the grids on screen kept the stage from before the write.
//
// A write therefore refetches every MOUNTED, enabled stage read (inactive ones are only marked
// stale), coalesced to one round per write burst (`queue-coalesced-invalidations.ts`). Each read is
// one indexed `grapheme IN (…)` lookup per overlay table, and structural sharing keeps an
// unchanged result's identity, so tiles whose stage didn't move don't re-render.
//
// Also handed to `useTableChangeVersion` as the registrations' `tables`, the dependency list
// `withWriteClockReadGuard` clocks before each read.
const STAGE_RELEVANT_TABLES = [
  "user_grapheme_meaning_card",
  "user_grapheme_pronunciation_card",
  "fsrs_card",
  "user_card",
] as const;

// Stage reads never need descriptions, glosses, row audit fields or the rest of the scheduler.
// Catalogs and row-sized pages share these projected entries, rather than retaining full actor
// rows once for every overlapping grapheme set. Direct relational reads preserve tombstone
// filtering; registered dependencies and createUseQueryOptions preserve committed-write checks.
const STAGE_ROW_PROJECTION = {
  columns: { grapheme: true },
  with: {
    userCard: {
      columns: { manuallyMasteredAt: true },
      with: { fsrsCard: { columns: { lastReview: true, scheduledDays: true } } },
    },
  },
} as const;

/**
 * The change-version wrapper for a stage read: any committed write to a stage-relevant table
 * invalidates the wrapped key (see `STAGE_RELEVANT_TABLES` for why it is not narrowed to the
 * key's graphemes).
 *
 * Shared by the overlay reads, the batched stage query and paged grids (`usePagedGraphemeTileData`),
 * whose infinite-query key covers the whole ordered set.
 */
export function useMorphemeStagesTableChangeVersion() {
  return useTableChangeVersion(tableNameMatcher(STAGE_RELEVANT_TABLES));
}

function useGetStageRowsQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const withStageVersion = useMorphemeStagesTableChangeVersion();
  return (kind: "meaning" | "pronunciation", uniqueGraphemes: readonly string[]) =>
    withStageVersion(["morphemeStageRows", kind, { actorDb, uniqueGraphemes }] as const);
}

const [useStageRowsQueryOptions, stageRowsQueryFn] = createUseQueryOptions(
  useGetStageRowsQueryKey,
  async ({ queryKey: [_, kind, { actorDb, uniqueGraphemes }] }): Promise<StagedOverlayRow[]> => {
    if (hasLength(uniqueGraphemes, 0)) {
      return [];
    }
    const where = { grapheme: { in: [...uniqueGraphemes] } };
    return kind === "meaning"
      ? actorDb(import.meta.location).query.userGraphemeMeaningCard.findMany({
          ...STAGE_ROW_PROJECTION,
          where,
        })
      : actorDb(import.meta.location).query.userGraphemePronunciationCard.findMany({
          ...STAGE_ROW_PROJECTION,
          // A pronunciation card is one reading's, which per-card stages key by its pinyin.
          columns: { grapheme: true, pinyin: true },
          where,
        });
  },
);

function useMeaningStageQuery(uniqueGraphemes: string[]) {
  return useStageRowsQueryOptions("meaning", uniqueGraphemes);
}

function usePronunciationStageQuery(uniqueGraphemes: string[]) {
  return useStageRowsQueryOptions("pronunciation", uniqueGraphemes);
}

/** Both overlay reads over one grapheme set, as a stage query's key carries them. */
type StageRowsQueryOptions = {
  meaningQueryOptions: ReturnType<typeof useMeaningStageQuery>;
  pronunciationQueryOptions: ReturnType<typeof usePronunciationStageQuery>;
};

function useStageRowsQueryOptionsForGraphemes(graphemes: readonly string[]): StageRowsQueryOptions {
  // Stable, deduped identity for the set, so equal sets share the overlay reads — with each other
  // and with paged grids over the same grapheme set.
  const uniqueGraphemes = uniq([...graphemes]).sort();
  return {
    meaningQueryOptions: useMeaningStageQuery(uniqueGraphemes),
    pronunciationQueryOptions: usePronunciationStageQuery(uniqueGraphemes),
  };
}

/** Re-run both projected reads through their registered, committed-write guarded entries. */
async function fetchStageRows(
  client: QueryClient,
  { meaningQueryOptions, pronunciationQueryOptions }: StageRowsQueryOptions,
) {
  const { uniqueGraphemes } = meaningQueryOptions.queryKey[2];
  if (hasLength(uniqueGraphemes, 0)) {
    return { meaningRows: [], pronunciationRows: [], uniqueGraphemes };
  }
  const [meaningRows, pronunciationRows] = await Promise.all([
    client.fetchQuery(meaningQueryOptions),
    client.fetchQuery(pronunciationQueryOptions),
  ]);
  return { meaningRows, pronunciationRows, uniqueGraphemes };
}

// The grapheme set appears in this key only through the two nested read keys: a catalog's set is
// ~8.5k glyphs, and every copy is re-hashed into a key string on each render and retained as the
// query hash.
type MorphemeListStagesQueryKey = readonly ["morphemeStages", StageRowsQueryOptions];

/** An overlay row joined to its study card, as both stage reads return it. */
export type StagedOverlayRow = {
  grapheme: string;
  /** A pronunciation card's reading. */
  pinyin?: string;
  userCard: StagedUserCard | null | undefined;
};

/** A study card's stage, or `null` for a card that is not in study. */
function getStudyCardStage(userCard: StagedUserCard | null | undefined): UserCardStage | null {
  // Removing/clearing a card from the deck deliberately preserves its `user_card` and grapheme
  // overlay so user-authored mnemonics survive. Deck membership is the FSRS relation (or manual
  // mastery), not the mere existence of that preserved row.
  if (userCard == null || (userCard.fsrsCard === null && userCard.manuallyMasteredAt === null)) {
    return null;
  }
  return getUserCardStage(userCard);
}

/**
 * Fold the two overlay reads into a `grapheme → stage` object over `uniqueGraphemes`. Every
 * requested grapheme is present; one with no active study card (or manual mastery) is `"unadded"`.
 *
 * Pure, so the batched hook below and the page reader (`useReadMorphemeStagesPage`) share one
 * definition of "a grapheme's stage" — the MIN stage across its meaning and pronunciation cards.
 */
export function buildMorphemeStagesByGrapheme(
  uniqueGraphemes: readonly string[],
  meaningRows: readonly StagedOverlayRow[],
  pronunciationRows: readonly StagedOverlayRow[],
): MorphemeStagesByGrapheme {
  const stageByGrapheme: Record<string, MorphemeGlyphTileStage> = Object.fromEntries(
    uniqueGraphemes.map((grapheme) => [grapheme, UNADDED_STAGE]),
  );

  const stagesByGrapheme = new Map<string, UserCardStage[]>();
  const appendStage = (grapheme: string, userCard: StagedUserCard | null | undefined) => {
    const stage = getStudyCardStage(userCard);
    if (stage === null) {
      return;
    }
    const list = stagesByGrapheme.get(grapheme) ?? [];
    list.push(stage);
    stagesByGrapheme.set(grapheme, list);
  };
  for (const row of meaningRows) {
    appendStage(row.grapheme, row.userCard);
  }
  for (const row of pronunciationRows) {
    appendStage(row.grapheme, row.userCard);
  }

  for (const [grapheme, stages] of stagesByGrapheme) {
    stageByGrapheme[grapheme] = getMorphemeGlyphTileStageFromUserCardStage(
      getMinUserCardStage(stages),
    );
  }
  return stageByGrapheme;
}

/**
 * A render-bound reader of stages for an arbitrary PAGE of graphemes, for infinite queries that
 * reveal a large grid a page at a time. Both projected overlay reads use the same registered,
 * write-clock guarded cache entries as the batched hook, so revealing another page reuses
 * previously resolved sets.
 *
 * The caller owns invalidation of the page sequence: wrap its infinite-query key with
 * `useMorphemeStagesTableChangeVersion`.
 */
export function useReadMorphemeStagesPage() {
  const getStageRowsQueryKey = useGetStageRowsQueryKey();

  return useCallback(
    async (
      client: QueryClient,
      graphemes: readonly string[],
    ): Promise<MorphemeStagesByGrapheme> => {
      const uniqueGraphemes = uniq([...graphemes]).sort();
      if (hasLength(uniqueGraphemes, 0)) {
        return {};
      }
      const [meaningRows, pronunciationRows] = await Promise.all([
        client.fetchQuery(
          queryOptions({
            queryFn: stageRowsQueryFn,
            queryKey: getStageRowsQueryKey("meaning", uniqueGraphemes),
          }),
        ),
        client.fetchQuery(
          queryOptions({
            queryFn: stageRowsQueryFn,
            queryKey: getStageRowsQueryKey("pronunciation", uniqueGraphemes),
          }),
        ),
      ]);
      return buildMorphemeStagesByGrapheme(uniqueGraphemes, meaningRows, pronunciationRows);
    },
    [getStageRowsQueryKey],
  );
}

/**
 * BATCHED read of a whole set of graphemes' SRS button stages: ONE `useQuery` (and ONE change
 * subscription) for the set, returning a `grapheme → stage` object. A grid that would otherwise
 * mount one `useQuery` per tile (hundreds of observers + change subscribers committing in a single
 * render — a JS-thread storm that serializes the whole page's queries behind it) instead lifts this
 * once at the parent and passes each tile its `stage` as a prop. Callers with a single grapheme pass
 * an array of one.
 *
 * The stage read covers the same meaning + pronunciation overlays in two set-based queries, and any
 * committed write to a stage-relevant table refetches it while it is mounted, so a rating or an
 * enrollment restages every grid on screen at once.
 *
 * The graphemes are de-duplicated and sorted into a stable query key so two grids over the same set
 * share a cache entry and a repeated grapheme within a level can't produce a duplicate key.
 */
export function useGetMorphemeListStagesQueryKey(graphemes: readonly string[]) {
  const stageRowsQueryOptions = useStageRowsQueryOptionsForGraphemes(graphemes);
  const withStageVersion = useMorphemeStagesTableChangeVersion();

  // Callers gate fetching with the observer's `enabled`, never through this key: function values
  // are omitted by TanStack's JSON-based key hash, while the Query object retains the first key it
  // was created with, so a focus callback here could leave an explicitly requested foreground
  // refetch executing with a stale, disabled closure from an earlier Browse mount.
  return () =>
    withStageVersion(["morphemeStages", stageRowsQueryOptions] as MorphemeListStagesQueryKey);
}

export const [useMorphemeListStagesQueryOptions, morphemeListStagesQueryFn] = createUseQueryOptions(
  useGetMorphemeListStagesQueryKey,
  async ({ client, queryKey }): Promise<MorphemeStagesByGrapheme> => {
    // Once TanStack invokes this function, always perform the read. The observer's `enabled`
    // option prevents automatic work while Browse is frozen; an explicit foreground refetch must
    // never be vetoed by behavior captured in an old cached Query object's key.
    const { meaningRows, pronunciationRows, uniqueGraphemes } = await fetchStageRows(
      client,
      queryKey[1],
    );
    return buildMorphemeStagesByGrapheme(uniqueGraphemes, meaningRows, pronunciationRows);
  },
);

export function useMorphemeListStagesQuery(graphemes: readonly string[]) {
  return useMorphemeListStagesQueryOptions(graphemes);
}

/**
 * One grapheme's stage per study card: its meaning card (`null` while it is not in study), and the
 * pronunciation card of each reading in study, by tone-number pinyin.
 */
export type MorphemeCardStages = {
  meaning: UserCardStage | null;
  pronunciationByPinyin: Readonly<Record<string, UserCardStage>>;
};

export type MorphemeCardStagesByGrapheme = Readonly<Record<string, MorphemeCardStages>>;

/**
 * The study card a tile stands for when it is not the grapheme as a whole: a meaning-only level's
 * tile is the meaning card, and a pronunciation level's tile the pronunciation card of its slot's
 * reading (or, without `pinyin`, the weakest reading in study).
 */
export type MorphemeTileCard = { kind: "meaning" } | { kind: "pronunciation"; pinyin?: string };

function weakerStage(stage: UserCardStage, other: UserCardStage | undefined): UserCardStage {
  return other === undefined ? stage : getMinUserCardStage([stage, other]);
}

/** Fold the two overlay reads into per-card stages for every one of `uniqueGraphemes`. */
export function buildMorphemeCardStagesByGrapheme(
  uniqueGraphemes: readonly string[],
  meaningRows: readonly StagedOverlayRow[],
  pronunciationRows: readonly StagedOverlayRow[],
): MorphemeCardStagesByGrapheme {
  const meaningByGrapheme = new Map<string, UserCardStage>();
  for (const row of meaningRows) {
    const stage = getStudyCardStage(row.userCard);
    if (stage !== null) {
      meaningByGrapheme.set(row.grapheme, weakerStage(stage, meaningByGrapheme.get(row.grapheme)));
    }
  }
  const pronunciationByGrapheme = new Map<string, Record<string, UserCardStage>>();
  for (const row of pronunciationRows) {
    const stage = getStudyCardStage(row.userCard);
    if (stage === null || row.pinyin === undefined) {
      continue;
    }
    const byPinyin = pronunciationByGrapheme.get(row.grapheme) ?? {};
    byPinyin[row.pinyin] = weakerStage(stage, byPinyin[row.pinyin]);
    pronunciationByGrapheme.set(row.grapheme, byPinyin);
  }
  return Object.fromEntries(
    uniqueGraphemes.map((grapheme): [string, MorphemeCardStages] => [
      grapheme,
      {
        meaning: meaningByGrapheme.get(grapheme) ?? null,
        pronunciationByPinyin: pronunciationByGrapheme.get(grapheme) ?? {},
      },
    ]),
  );
}

/** The tile stage of `card` among a grapheme's `stages`: `"unadded"` while it is not in study. */
export function getMorphemeCardStage(
  stages: MorphemeCardStages | undefined,
  card: MorphemeTileCard,
): MorphemeGlyphTileStage {
  if (stages === undefined) {
    return UNADDED_STAGE;
  }
  if (card.kind === "meaning") {
    return stages.meaning === null
      ? UNADDED_STAGE
      : getMorphemeGlyphTileStageFromUserCardStage(stages.meaning);
  }
  if (card.pinyin !== undefined) {
    const stage = stages.pronunciationByPinyin[card.pinyin];
    return stage === undefined ? UNADDED_STAGE : getMorphemeGlyphTileStageFromUserCardStage(stage);
  }
  const readingStages = Object.values(stages.pronunciationByPinyin);
  return hasLength(readingStages, 0)
    ? UNADDED_STAGE
    : getMorphemeGlyphTileStageFromUserCardStage(getMinUserCardStage(readingStages));
}

type MorphemeListCardStagesQueryKey = readonly ["morphemeCardStages", StageRowsQueryOptions];

/**
 * BATCHED read of a grapheme set's per-card stages (`MorphemeCardStages`), for grids whose tiles
 * each stand for one card (`MorphemeTileCard`). It shares both overlay reads, and their
 * invalidation, with `useMorphemeListStagesQuery` over the same set.
 */
function useGetMorphemeListCardStagesQueryKey(graphemes: readonly string[]) {
  const stageRowsQueryOptions = useStageRowsQueryOptionsForGraphemes(graphemes);
  const withStageVersion = useMorphemeStagesTableChangeVersion();
  return () =>
    withStageVersion([
      "morphemeCardStages",
      stageRowsQueryOptions,
    ] as MorphemeListCardStagesQueryKey);
}

export const [useMorphemeListCardStagesQuery] = createUseQueryOptions(
  useGetMorphemeListCardStagesQueryKey,
  async ({ client, queryKey }): Promise<MorphemeCardStagesByGrapheme> => {
    const { meaningRows, pronunciationRows, uniqueGraphemes } = await fetchStageRows(
      client,
      queryKey[1],
    );
    return buildMorphemeCardStagesByGrapheme(uniqueGraphemes, meaningRows, pronunciationRows);
  },
);
